import {AspectRatio} from '@contentlab/types/aspectRatio';
import assetFetchers from '@/api/fetchers/content-studio/asset';
import {Asset} from '@/views/content-studio/components/asset-table/types';
import useInfiniteReactQuery from '@/hooks/use-infinite-react-query';
import {CONTENT_LAB, ASSETS} from '../consts';

export type AssetsQuerySorting = {
  field: string;
  desc: boolean;
};

export type AssetsQueryFilters = {
  type: 'uploads' | 'results';
  project?: string[];
  source?: string[];
  language?: string[];
  format?: ('16:9' | '9:16' | '1:1' | '4:5')[];
  name?: string;
  ids?: string[];
  startDate?: Date;
  endDate?: Date;
};

function useAssetsQuery(
  sorting: AssetsQuerySorting,
  filters: AssetsQueryFilters,
  shouldPoll?: boolean
) {
  return useInfiniteReactQuery<Asset[]>({
    queryKey: [CONTENT_LAB, ASSETS, sorting, filters],
    queryFn: async (): Promise<Asset[]> => {
      const response = await assetFetchers.getAssets({
        sorting: {
          field: sorting.field,
          order: sorting.desc ? 'desc' : 'asc',
        },
        filters: {
          folder: filters.project,
          source: filters.source,
          language: filters.language,
          aspect_ratio: filters.format as AspectRatio[],
          name: filters.name,
          ids: filters.ids,
          type: filters.type === 'uploads' ? 'upload' : 'result',
        },
      });

      const assets = response.assets.map((asset) => ({
        ...asset,
        metadata: {
          durationMillis: asset.metadata.durationMillis,
          fileSizeBytes: asset.metadata.fileSizeBytes,
          height: asset.metadata.height,
          width: asset.metadata.width,
          language: asset.metadata.language || undefined,
          aspectRatio: asset.metadata.aspectRatio as AspectRatio,
        },
      }));

      return assets;
    },
    refetchInterval: shouldPoll ? 5000 : false,
  });
}

export default useAssetsQuery;
